import { useEffect, useState } from 'react';
import cx from 'classnames';
import { LoadingOverlay, EnterpriseHomeBackground } from '@airtm/airtm-ui';
import * as compose from 'lodash.flowright';
import MainErrorBoundary from 'components/ErrorBoundaries/MainErrorBoundary';
import useMe from 'utils/hooks/useMe/useMe';
import JwtManager from 'lib/JwtManager';
import { resetNotifications } from 'utils/apollo';
import withCorporateDocumentation from 'hoc/withCorporateDocumentation';
import withPartialStatus from 'hoc/withPartialStatus';
import withInvalidStatus, { useInvalidPayoutStatus } from 'hoc/withInvalidPayoutStatus';
import withInvalidProvider from 'hoc/withInvalidProvider';
import withEnable2FA from 'hoc/withEnable2FA';
import useUserLanguage from './useUserLanguage';
import useGuestRedirection from './useGuestRedirection';
import { NotificationsProvider } from './Notifications/Notifications';
import Footer from './Footer';
import Header from './Header/Header';
import Navbar from './Navbar/Navbar';
import Routes from './Routes';

type Props = {
  location: {
    pathname: string;
  };
};

const Layout = function ({ location }: Props) {
  const [showSideNav, setShowSideNav] = useState(false);

  // Main me query
  const { me, loading } = useMe({
    fetchPolicy: 'cache-first',
    include: {
      securityHub: true,
      numbers: true,
      preferences: true,
    },
    skip: !JwtManager.jwt,
  });
  const userId = me?.id;
  useUserLanguage();
  useGuestRedirection({ userId, loading });
  const { alerts: payoutAlerts } = useInvalidPayoutStatus();
  useEffect(() => {
    if (userId) {
      // Reset notifications to prevent re-hydration from the cache
      resetNotifications();
    }
  }, [userId]);

  const isHomePage = location.pathname === '/home';
  const isFindTalentPage = location.pathname === '/airner-intelligence/find-talent';
  const showSectionClass = !isHomePage;
  const showAlerts = !!payoutAlerts?.length;

  return (
    <NotificationsProvider>
      <div id="page" className={cx({ 'd-block': window.RENDER_ON_APP })}>
        {!window.RENDER_ON_APP && (
          <>
            <div id="navigation">
              <Navbar
                className={cx({ 'hide-nav': !showSideNav })}
                onItemClicked={() => setShowSideNav(false)}
              />
            </div>
            {isHomePage && <EnterpriseHomeBackground style={{ height: '139px' }} />}
            <Header
              onToggleSideNav={(isShow: boolean) => setShowSideNav(isShow)}
              showMobileNav={showSideNav}
            />
          </>
        )}

        <div id="content">
          <MainErrorBoundary errorTag="PageLevelError">
            <>
              {showAlerts && payoutAlerts}
              <div className={cx({ container: !isFindTalentPage })}>
                <section className={cx({ section: showSectionClass }, { 'u-pt-3': showAlerts })}>
                  {userId ? <Routes /> : <LoadingOverlay />}
                </section>
              </div>
            </>
          </MainErrorBoundary>
        </div>
        <Footer className={cx({ 'd-none': isFindTalentPage })} />
      </div>
    </NotificationsProvider>
  );
};

export default compose(
  withPartialStatus,
  withCorporateDocumentation,
  withInvalidProvider,
  withEnable2FA,
  withInvalidStatus,
)(Layout);
